
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import StackedBarChartFilterable from '../../filterables/StackedBarChartFilterable.vue';
import DateFilter from '@/components/reports-v3/components/filters/DateFilter.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, sqlExtractYear, sqlExtractMonth, sqlExtractDay, sqlSum, sqlSumByDocType, getTableName } from '@/worker/db/sqlHelpers';
import { PermissionsGroup } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import moment from 'moment';
import { GobiColor } from '@/helpers/color';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';

@Component({
	components: {
        IncludeDebitNoteToggle,
		StackedBarChartFilterable,
		DateFilter,
		SelectFilter,
		IncludeCreditNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SalesPeriodComparisonLineChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales', 'customers'];
	}

	public filterIds: Array<
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'compareDate1'
		| 'compareDate2'
		| 'comparisonPeriod'
	> = [
		'customers',
		'agents',
		'compareDate1',
		'compareDate2',
		'comparisonPeriod',
	];

	public xAxisData: string[] = [];
	public series: echarts.ECharts[] | any = [];
	public selectedDate1 = moment().startOf('month').add(-1, 'month').valueOf();
	public selectedDate2 = moment()
		.startOf('month')
		.add(-1, 'month')
		.add(-1, 'year')
		.valueOf();
	public period: '31 days' | '12 months' | '24 months' = '31 days';
	public periodOptions = ['31 days', '12 months', '24 months'];

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get selectedDateRange1(): [number, number] {
		const period = this.period || '1 month';
		const periodArgument = period.split(' ');
		return [
			moment(this.selectedDate1).startOf('day').valueOf(),
			moment(this.selectedDate1)
				.add(...periodArgument)
				.endOf('day')
				.valueOf(),
		];
	}

	public get selectedDateRange2(): [number, number] {
		const period = this.period || '1 month';
		const periodArgument = period.split(' ');
		return [
			moment(this.selectedDate2).startOf('day').valueOf(),
			moment(this.selectedDate2)
				.add(...periodArgument)
				.endOf('day')
				.valueOf(),
		];
	}

	public get periodArgument(): [number, 'days' | 'months'] {
		const period = this.period || '1 month';
		const pa = period.split(' ');
		return [parseInt(pa[0], 10), pa[1] as 'days' | 'months'];
	}

	public get xAxisLabel() {
		return this.periodArgument[1] === 'months' ? 'Month' : 'Day';
	}

	public get formatter() {
		const pa = this.periodArgument;
		return pa[1] === 'days'
			? TooltipFormatter.comparisonDay
			: TooltipFormatter.comparisonMonth;
	}

	public get expensiveHook() {
		const {
			periodArgument,
			selectedDateRange1,
			selectedDateRange2,
            includeDebitNote,
			includeCreditNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			periodArgument,
			selectedDateRange1,
			selectedDateRange2,
            includeDebitNote,
			includeCreditNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const queryHead = await this._ensureTablesLoaded(
			['sales', 'creditNotes', 'customerTrades'] as any[],
		);
		this.checkInterrupted(jobId);

		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');

		const periodType = this.periodArgument[1] === 'days' ? 'day' : 'month';
		const n = this.periodArgument[0];

		// Build period SQL parts
		const isDay = periodType === 'day';
		const selectFields = isDay
			? `${sqlExtractYear(COL.DOCDATE)}, ${sqlExtractMonth(COL.DOCDATE)}, ${sqlExtractDay(COL.DOCDATE)}`
			: `${sqlExtractYear(COL.DOCDATE)}, ${sqlExtractMonth(COL.DOCDATE)}`;
		const groupBy = isDay ? 'year, month, day' : 'year, month';

		const dr1Filter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange1[0]} AND ${this.selectedDateRange1[1]}`;
		const dr2Filter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange2[0]} AND ${this.selectedDateRange2[1]}`;

		// Build period keys for alignment
		const buildPeriodKeys = (dateRange: [number, number]): string[] => {
			const keys: string[] = [];
			const start = moment(dateRange[0]);
			const end = moment(dateRange[1]);
			if (isDay) {
				const current = start.clone().startOf('day');
				while (current.isSameOrBefore(end, 'day') && keys.length < n) {
					keys.push(`${current.year()}-${current.month() + 1}-${current.date()}`);
					current.add(1, 'day');
				}
			} else {
				const current = start.clone().startOf('month');
				while (current.isSameOrBefore(end, 'month') && keys.length < n) {
					keys.push(`${current.year()}-${current.month() + 1}`);
					current.add(1, 'month');
				}
			}
			return keys;
		};

		const period1Keys = buildPeriodKeys(this.selectedDateRange1);
		const period2Keys = buildPeriodKeys(this.selectedDateRange2);

		let data1: number[] = [];
		let data2: number[] = [];

		if (this.slPhModuleOnly) {
			// ========================================
			// SL Mode - use sales + creditNotes tables
			// ========================================

			const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
			const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

			const slMonthQueryForTable = (table: string, filter: string) => `
				SELECT ${selectFields}, ${sqlSum(COL.AMOUNT, 'totalAmt')}
				FROM ${table}
				WHERE ${filter}
				GROUP BY ${groupBy}
				ORDER BY ${groupBy}
			`;

			this.generateLoadingText(0.4, 0.9);

			const [s1Rows, s2Rows, cn1Rows, cn2Rows] = await Promise.all([
				queryHead.rawQuery(slMonthQueryForTable(salesTable, `${dr1Filter}${docTypeFilter}`)),
				queryHead.rawQuery(slMonthQueryForTable(salesTable, `${dr2Filter}${docTypeFilter}`)),
				queryHead.rawQuery(slMonthQueryForTable(cnTable, dr1Filter)),
				queryHead.rawQuery(slMonthQueryForTable(cnTable, dr2Filter)),
			]);
			this.checkInterrupted(jobId);

			const toMap = (rows: any[]) => {
				const m = new Map<string, number>();
				for (const r of rows) {
					const key = isDay
						? `${r.year}-${r.month}-${r.day}`
						: `${r.year}-${r.month}`;
					m.set(key, Number(r.totalAmt) || 0);
				}
				return m;
			};
			const s1Map = toMap(s1Rows);
			const s2Map = toMap(s2Rows);
			const cn1Map = toMap(cn1Rows);
			const cn2Map = toMap(cn2Rows);

			data1 = period1Keys.map((key) => {
				const sales = s1Map.get(key) || 0;
				const cn = this.includeCreditNote ? (cn1Map.get(key) || 0) : 0;
				return sales - cn;
			});
			data2 = period2Keys.map((key) => {
				const sales = s2Map.get(key) || 0;
				const cn = this.includeCreditNote ? (cn2Map.get(key) || 0) : 0;
				return sales - cn;
			});
		} else {
			// ========================================
			// AR Mode - use customerTrades table
			// ========================================

			const arQueryForFilter = (filter: string) => `
				SELECT ${selectFields},
					${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
					${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
					${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')}
				FROM ${arTable}
				WHERE ${filter}
				GROUP BY ${groupBy}
				ORDER BY ${groupBy}
			`;

			this.generateLoadingText(0.4, 0.9);

			const [ar1Rows, ar2Rows] = await Promise.all([
				queryHead.rawQuery(arQueryForFilter(dr1Filter)),
				queryHead.rawQuery(arQueryForFilter(dr2Filter)),
			]);
			this.checkInterrupted(jobId);

			const toArMap = (rows: any[]) => {
				const m = new Map<string, { iv: number; dn: number; cn: number }>();
				for (const r of rows) {
					const key = isDay
						? `${r.year}-${r.month}-${r.day}`
						: `${r.year}-${r.month}`;
					m.set(key, {
						iv: Number(r.iv_amount) || 0,
						dn: Number(r.dn_amount) || 0,
						cn: Number(r.cn_amount) || 0,
					});
				}
				return m;
			};
			const ar1Map = toArMap(ar1Rows);
			const ar2Map = toArMap(ar2Rows);

			data1 = period1Keys.map((key) => {
				const d = ar1Map.get(key);
				if (!d) { return 0; }
				return d.iv + (this.includeDebitNote ? d.dn : 0) - (this.includeCreditNote ? d.cn : 0);
			});
			data2 = period2Keys.map((key) => {
				const d = ar2Map.get(key);
				if (!d) { return 0; }
				return d.iv + (this.includeDebitNote ? d.dn : 0) - (this.includeCreditNote ? d.cn : 0);
			});
		}

		this.generateLoadingText(1);

		// Generate x-axis labels showing both periods' dates (dd vs dd or MMM vs MMM)
		// Skip "vs" if both values are the same
		this.xAxisData = [...Array(n)].map((item, i) => {
			if (isDay) {
				const date1 = moment(this.selectedDate1).add(i, 'days').format('DD');
				const date2 = moment(this.selectedDate2).add(i, 'days').format('DD');
				return date1 === date2 ? date1 : `${date1} vs ${date2}`;
			} else {
				const month1 = moment(this.selectedDate1).add(i, 'months').format('MMM');
				const month2 = moment(this.selectedDate2).add(i, 'months').format('MMM');
				if (month1 === month2) {
					return month1;
				}
				const month1WithYear = moment(this.selectedDate1).add(i, 'months').format('MMM YY');
				const month2WithYear = moment(this.selectedDate2).add(i, 'months').format('MMM YY');
				return `${month1WithYear} vs ${month2WithYear}`;
			}
		});

		// Ensure data arrays match the expected period length
		const slicedData1 = data1.slice(0, n);
		const slicedData2 = data2.slice(0, n);

		this.series = [
			{
				name: moment(this.selectedDate1).format('DD MMM YYYY'),
				type: 'bar',
				smooth: true,
				barWidth: '40%',
				color: GobiColor.COLOR1,
				data: slicedData1,
			},
			{
				name: moment(this.selectedDate2).format('DD MMM YYYY'),
				type: 'bar',
				smooth: true,
				barWidth: '40%',
				color: GobiColor.COLOR3,
				data: slicedData2,
			},
		];

		this.saveHistory('series', 'xAxisData');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
